import { Link } from "react-router-dom";
import { NerdStackFooter } from "../components/NerdStackFooter.jsx";
import SuiteMark from "../components/SuiteMark.jsx";
import { parentGradient } from "../components/toolGradients.js";

// V56 — 9 suites in a 3×3 keypad layout. Position 1-9 reads like a phone
// keypad: stack/tools/bio (row 1), chem/math/phys (row 2),
// music/history/games (row 3). Letter-count is no longer a layout principle;
// it's just a uniform grid. Single SUITES array drives the whole launcher.
//
// History badge switched from "Hi" → "H" in V56 — fits the keypad rhythm and
// keeps the wordmark cleaner at small sizes (see SuiteMark.jsx LETTER map).
const SUITES = [
  // Row 1
  { suite: "stack",   name: "NerdStack",   to: "/stack",   sub: "Study utilities" },
  { suite: "tools",   name: "NerdTools",   to: "/tools",   sub: "Handy tools" },
  { suite: "bio",     name: "NerdBio",     to: "/bio",     sub: "Biology" },
  // Row 2
  { suite: "chem",    name: "NerdChem",    to: "/chem",    sub: "Chemistry" },
  { suite: "math",    name: "NerdMath",    to: "/math",    sub: "Math" },
  { suite: "phys",    name: "NerdPhys",    to: "/phys",    sub: "Physics" },
  // Row 3
  { suite: "music",   name: "NerdMusic",   to: "/music",   sub: "Music theory" },
  { suite: "history", name: "NerdHist", to: "/history", sub: "History" },
  { suite: "games",   name: "NerdGames",   to: "/games",   sub: "Learning games" },
];

function SuiteTile({ s }) {
  const { from } = parentGradient(s.suite);
  const cls = "stile-" + s.suite;
  return (
    <Link to={s.to} style={{ textDecoration: "none", color: "inherit", display: "flex", flexDirection: "column", alignItems: "center" }} className={cls}>
      <style>{`
        .${cls} .stile-tile { transition: transform .14s ease, filter .18s ease; }
        @media (hover: hover) {
          .${cls}:hover .stile-tile { transform: translateY(-3px); filter: drop-shadow(0 6px 16px ${from}66); }
        }
      `}</style>
      <div className="stile-tile" style={{
        width: "100%", aspectRatio: "1 / 1",
        maxWidth: "clamp(88px, 26vw, 132px)",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <div style={{ width: "100%", height: "100%" }}>
          <div style={{ width: "100%", height: "100%" }} className="suitemark-fluid">
            <SuiteMark suite={s.suite} size={132} />
          </div>
        </div>
      </div>
      <div style={{ marginTop: 12, textAlign: "center" }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: "#E8E9F3" }}>{s.name}</div>
        <div style={{ fontSize: 11, color: "#7C8099", marginTop: 2 }}>{s.sub}</div>
      </div>
    </Link>
  );
}

function Row({ items }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "clamp(16px, 2.5vw, 36px)", marginBottom: "clamp(20px, 3vw, 36px)" }}>
      {items.map((s) => <SuiteTile key={s.suite} s={s} />)}
    </div>
  );
}

// V56 — NerdSuite hero wordmark. Unified lavender gradient (light TL → deep
// violet BR), matching the locked treatment from the v5 mockup. The whole
// word shares one <linearGradient>, so it reads as a single coherent mark
// rather than nine fragmented letters.
function HeroWordmark() {
  return (
    <div style={{ display: "flex", justifyContent: "center", width: "100%" }}>
      <svg
        width="clamp(220px, 38vw, 380px)"
        viewBox="0 0 380 76"
        xmlns="http://www.w3.org/2000/svg"
        style={{ display: "block", height: "auto" }}
        aria-label="NerdSuite"
      >
        <defs>
          <linearGradient id="ns-wordmark" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#C4B5FD" />
            <stop offset="1" stopColor="#7C3AED" />
          </linearGradient>
        </defs>
        <text
          x="190"
          y="56"
          textAnchor="middle"
          fontFamily="'DM Sans', system-ui, sans-serif"
          fontSize="56"
          fontWeight="800"
          letterSpacing="-0.5"
          fill="url(#ns-wordmark)"
        >
          NerdSuite
        </text>
      </svg>
    </div>
  );
}

export default function SuiteLauncher() {
  return (
    <div style={{ minHeight: "100vh", background: "#0A0B14", color: "#E8E9F3", fontFamily: "'DM Sans', system-ui, sans-serif", position: "relative", overflow: "hidden" }}>
      <style>{`
        .suitemark-fluid > svg { width: 100% !important; height: 100% !important; display: block; }
      `}</style>
      {/* V56 — Lavender radial glow at bottom-center, matching the suite-landing
          pattern but with NerdSuite's master color (lavender #A78BFA). */}
      <div style={{
        position: "fixed",
        bottom: "-30%",
        left: "50%",
        transform: "translateX(-50%)",
        width: "60vw",
        maxWidth: 600,
        height: "60vw",
        maxHeight: 600,
        background: "radial-gradient(circle, rgba(167,139,250,0.16) 0%, transparent 65%)",
        pointerEvents: "none",
        borderRadius: "50%",
        zIndex: 0,
      }} />
      <main style={{ maxWidth: "min(1040px, 92vw)", margin: "0 auto", padding: "clamp(48px,7vw,88px) clamp(20px,4vw,40px) 60px", position: "relative", zIndex: 1 }}>
        <HeroWordmark />
        <p style={{ fontSize: 13.5, color: "#9A9DBE", textAlign: "center", margin: "10px 0 clamp(32px, 5vw, 52px)" }}>
          Free study tools. Fast. Actually good.
        </p>
        <Row items={SUITES} />
      </main>
      <NerdStackFooter />
    </div>
  );
}
